import { format, isValid } from "date-fns";
import { useCallback, useMemo } from "react";

import { toast } from "@performyard/py-components";

// store
import useStore from "../../../../store";

import { useCohorts, usePageSettings, useQuestionsSummary } from "../../../../../../api/hooks";

import {
	getCohorts,
	getEngagementSurveyQuestionAnswers,
	getNPSAdditionalFeedback,
} from "../../../../../../api/services";

import downloadCSV from "../../../../../../utils/downloadCSV";
import escapeCsvField from "../../../../../../utils/escapeCsvField";

// config
import Config from "../../../../Config";
import { IEngagementFilter } from "../../../../schema";
import type {
	IEngagementQuestionAnswersResponse,
	INPSAdditionalFeedbackResponse,
	SummaryResponse,
} from "../../../../schema/defs/common";

export type DistributionData = {
	total_response_count: number;
	score_to_count: Record<1 | 2 | 3 | 4 | 5, number>;
};

export type QuestionRow = {
	id: string;
	question_id: string;
	factor: string;
	question_text: string;
	average_score: number | null;
	distribution: DistributionData;
	total_response_count: number;
	scale: string;
	type: string;
	included_in_satisfaction: boolean;
	enabled: boolean;
};

const { DEFAULT_COHORT_SELECTION, DEFAULT_FACTOR_SELECTION } = Config.QUESTIONS.TABLE;

// cohort _id is typed ObjectId in some sources but is a plain string at runtime
type CohortRef = { _id: unknown; name: string };

const resolveSelectedCohort = (
	storeCohort: string | null,
	persistedCohort: string | null | undefined,
	cohorts: { _id: unknown }[] | undefined
): string => {
	if (storeCohort !== null) return storeCohort;
	if (persistedCohort && (!cohorts || cohorts.some((c) => String(c._id) === persistedCohort))) {
		return persistedCohort;
	}
	return DEFAULT_COHORT_SELECTION;
};

const cohortNameFromList = (cohortId: string, cohorts: CohortRef[]): string | undefined =>
	[{ _id: "all", name: "All Employees" }, ...cohorts].find((c) => String(c._id) === cohortId)?.name;

const buildCohortNameMap = (cohorts: CohortRef[]): Record<string, string> =>
	cohorts.reduce(
		(acc, cohort) => {
			acc[String(cohort._id)] = cohort.name;
			return acc;
		},
		{} as Record<string, string>
	);

export const scoresToPercentages = (distributionData: DistributionData): Record<1 | 2 | 3 | 4 | 5, number> => {
	const { total_response_count, score_to_count } = distributionData;
	return Object.entries(score_to_count).reduce(
		(acc, [score, count]) => {
			acc[score] = (count / total_response_count) * 100;
			return acc;
		},
		{ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
	);
};

const getTimePeriodLabel = (filter: IEngagementFilter): string => {
	const timePeriodOption = Config.FILTER_OPTIONS.find((option) => option.value === filter.value);
	if (timePeriodOption) return timePeriodOption.label;
	if (filter.startDate && filter.endDate) {
		return `${filter.startDate.toDateString()} to ${filter.endDate.toDateString()}`;
	}
	return "Custom Time Period";
};

/**
 * Hook to make an api call based on changes the engagement date range filter state
 * and return rows formatted for the QuestionsTable component
 */
const useQuestionTableRows = (
	searchTerm: string
): {
	rows: QuestionRow[];
	isLoading: boolean;
	getCSVContent: () => string | undefined;
	selectCSVDownloadName: () => string;
	handleDownloadQuestionAnswers: (questionId: string) => void;
	selectedCohort: string;
	filter: IEngagementFilter;
	handleDownloadNPSAdditionalFeedback: () => void;
} => {
	const [filter, storeCohort] = useStore((state) => [state.filter, state.questions.cohort]);
	const { data: pageSettingsData } = usePageSettings();
	const { data: cohortData } = useCohorts();
	const persistedCohort = pageSettingsData?.questions?.allQuestionsTable?.cohort;
	const selectedCohort = resolveSelectedCohort(storeCohort, persistedCohort, cohortData?.cohorts);
	const selectedFactor = pageSettingsData?.questions?.allQuestionsTable?.factor || DEFAULT_FACTOR_SELECTION;

	const cohortName = useMemo(
		() => cohortNameFromList(selectedCohort, cohortData?.cohorts || []),
		[selectedCohort, cohortData]
	);

	// the cell may hold a closure from before the cohorts query resolved, leaving the
	// CSV's cohort names blank — fetch the list at call time if it isn't loaded yet
	const getCohortsForExport = async (): Promise<CohortRef[]> =>
		cohortData?.cohorts ?? (await getCohorts("active")).cohorts ?? [];

	const { data: questionsData, isLoading } = useQuestionsSummary({
		startDate: filter.startDate,
		endDate: filter.endDate,
		cohortIds: selectedCohort !== "all" ? selectedCohort : undefined,
		factor: selectedFactor !== "All Factors" ? selectedFactor : undefined,
		search: searchTerm,
	});

	const questionRows: QuestionRow[] = useMemo(() => {
		return (
			questionsData?.data.map((question) => ({
				id: question._id,
				question_id: question._id,
				factor: question.factor,
				question_text: question.question_text,
				average_score: question.average_score ? parseFloat(question.average_score.toFixed(1)) : null,
				distribution: question.distribution as DistributionData,
				total_response_count: question.distribution.total_response_count ?? 0,
				scale: question.scale,
				type: question.type || "Core",
				included_in_satisfaction: question.included_in_satisfaction ?? true,
				enabled: question.enabled,
			})) || []
		);
	}, [questionsData]);

	const selectCSVDownloadName = useCallback(() => {
		const date = new Date().toDateString();
		const middleText = `${cohortName}${searchTerm.length ? `-Search By '${searchTerm}'` : ""}`;

		return `PerformYard-Engagement-Questions-${getTimePeriodLabel(filter)}-${middleText}-${date}.csv`;
	}, [cohortName, searchTerm, filter.value]);

	const getCSVContent = () => {
		if (!questionsData) return;
		const csvColumns = [
			"Type",
			"Factor",
			"Question Text",
			"# of Responses",
			"Average Score",
			"Strongly Disagree (1)",
			"Disagree (2)",
			"Neither Agree nor Disagree (3)",
			"Agree (4)",
			"Strongly Agree (5)",
		];
		const csvRows = questionsData.data.map((question) => {
			const scoresMap = scoresToPercentages(question.distribution as DistributionData);
			return [
				question.type || "Core",
				escapeCsvField(question.factor),
				escapeCsvField(question.question_text),
				question.distribution.total_response_count,
				question.average_score ? parseFloat(question.average_score.toFixed(1)) : "",
				...[1, 2, 3, 4, 5].map((score) => {
					if (scoresMap[score] === 0) return "";
					// round up to 1% if less than 1%
					const wholeNumberPercentage = Math.max(Math.round(scoresMap[score]), 1);
					return `${wholeNumberPercentage}%`;
				}),
			];
		});
		return [csvColumns, ...csvRows].map((row) => row.join(",")).join("\n");
	};

	const handleDownloadQuestionAnswers = async (questionId: string) => {
		// resolve filters at call time: the options cell can invoke a closure from an
		// earlier render via AG Grid's context snapshot, so render-scope values may be
		// stale by the time the user clicks Download CSV (PY-13187)
		const { filter: currentFilter, questions } = useStore.getState();
		const currentCohort = resolveSelectedCohort(questions.cohort, persistedCohort, cohortData?.cohorts);

		let data: IEngagementQuestionAnswersResponse[];
		let cohorts: CohortRef[];
		try {
			let answersResponse: SummaryResponse<IEngagementQuestionAnswersResponse[]>;
			[answersResponse, cohorts] = await Promise.all([
				getEngagementSurveyQuestionAnswers(
					questionId,
					currentFilter.startDate || undefined,
					currentFilter.endDate || undefined,
					currentCohort !== "all" ? currentCohort : undefined
				),
				getCohortsForExport(),
			]);
			data = answersResponse?.data ?? [];
		} catch {
			toast.error("Failed to download CSV. Please try again.");
			return;
		}

		const questionText = questionsData?.data?.find((question) => question._id === questionId)?.question_text || "";
		const cohortMap = buildCohortNameMap(cohorts || []);
		const csvColumns = ["Question Text", "Answer", "Cohorts"].join(",") + "\n";

		const csvContent = data
			.filter((row) => row.value !== "")
			.map((row) => {
				const questionTextEscaped = escapeCsvField(questionText);
				const valueEscaped = escapeCsvField(String(row.value));
				const cohortsEscaped = escapeCsvField(
					String(row.cohort_ids.map((cohortId) => cohortMap[String(cohortId)] || "").join("; "))
				);
				return `${questionTextEscaped},${valueEscaped},${cohortsEscaped}`;
			})
			.join("\n");

		const date = new Date().toDateString();
		const filename = `PerformYard-Engagement-Question-${questionText}-${getTimePeriodLabel(currentFilter)}-${cohortNameFromList(currentCohort, cohorts) ?? "All Employees"}-${date}.csv`;

		downloadCSV(csvColumns + csvContent, filename);
	};

	const handleDownloadNPSAdditionalFeedback = async () => {
		// same call-time resolution as handleDownloadQuestionAnswers
		const { filter: currentFilter, questions } = useStore.getState();
		const currentCohort = resolveSelectedCohort(questions.cohort, persistedCohort, cohortData?.cohorts);

		let additionalFeedback: INPSAdditionalFeedbackResponse[];
		let cohorts: CohortRef[];
		try {
			let feedbackResponse: SummaryResponse<INPSAdditionalFeedbackResponse[]>;
			[feedbackResponse, cohorts] = await Promise.all([
				getNPSAdditionalFeedback({
					startDate: currentFilter.startDate || undefined,
					endDate: currentFilter.endDate || undefined,
					cohortIds: currentCohort !== "all" ? currentCohort : undefined,
				}),
				getCohortsForExport(),
			]);
			additionalFeedback = feedbackResponse?.data ?? [];
		} catch {
			toast.error("Failed to download CSV. Please try again.");
			return;
		}

		const cohortMap = buildCohortNameMap(cohorts || []);

		const getCSVContent = () => {
			const csvColumns = ["eNPS Score", "Additional Feedback", "Cohorts", "Response Date"];

			const csvRows = additionalFeedback.length
				? additionalFeedback.map((feedback) => {
						const submittedAtDate = new Date(feedback.submitted_at);
						let formattedSubmittedAtDate = "";
						if (isValid(submittedAtDate)) {
							formattedSubmittedAtDate = format(submittedAtDate, "dd-MMM-yyyy");
						} else {
							formattedSubmittedAtDate = "";
						}
						const cohortNames = escapeCsvField(
							(feedback.cohort_ids || [])
								.map((cohortId) => cohortMap[String(cohortId)] || "")
								.filter(Boolean)
								.sort((a, b) => a.localeCompare(b))
								.join("; ")
						);
						return [
							feedback.enps_value,
							escapeCsvField(feedback.additional_feedback),
							cohortNames,
							formattedSubmittedAtDate,
						];
					})
				: [csvColumns.map(() => "")];

			return [csvColumns, ...csvRows].join("\n");
		};

		const date = new Date().toDateString();
		const filename = `PerformYard-Engagement-eNPS-AdditionalFeedback-${getTimePeriodLabel(currentFilter)}-${cohortNameFromList(currentCohort, cohorts) ?? "All Employees"}-${date}.csv`;
		const csvContent = getCSVContent();

		downloadCSV(csvContent, filename);
	};

	return {
		rows: questionRows,
		isLoading,
		getCSVContent,
		selectCSVDownloadName,
		handleDownloadQuestionAnswers,
		selectedCohort,
		filter,
		handleDownloadNPSAdditionalFeedback,
	};
};

export default useQuestionTableRows;
